import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { EmptyState } from "@/components/ui/empty-state";

interface Process {
  processId: string;
  name: string;
  totalForms?: number;
  applicationCount?: number;
  applicantProcesses?: unknown[];
}

interface Group {
  groupName: string;
  groupId: string;
  processes: Process[];
}

export interface ApplicationsListMeta {
  totalProcesses: number;
  totalPages: number;
  page: number;
  limit: number;
}

interface ApplicationsListProps {
  data: Group[];
  isLoading: boolean;
  isError: boolean;
  title: string;
  loadingMessage: string;
  errorMessage: string;
  searchPlaceholder: string;
  baseRoute: string;
  noDataMessage?: string;
  meta?: ApplicationsListMeta;
  search?: string;
  onSearchChange?: (search: string) => void;
  onPageChange?: (page: number) => void;
}

const DEBOUNCE_MS = 400;

const ApplicationsList: React.FC<ApplicationsListProps> = ({
  data,
  isLoading,
  isError,
  title,
  loadingMessage,
  errorMessage,
  searchPlaceholder,
  baseRoute,
  noDataMessage,
  meta,
  search,
  onSearchChange,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState(search ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (search !== undefined) setSearchInput(search);
  }, [search]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearchChange?.(value);
        debounceRef.current = null;
      }, DEBOUNCE_MS);
    },
    [onSearchChange]
  );

  const groupedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data;
  }, [data]);

  if (isLoading) {
    return <div className="text-center py-8 text-gray-700">{loadingMessage}</div>;
  }

  if (isError) {
    return (
      <p className="mt-8 text-center text-red-600">
        {errorMessage}
      </p>
    );
  }

  return (
    <main className="py-4 max-w-screen-md mx-auto">
      <h1 className="text-2xl font-semibold mb-4 text-center text-primary">
        {title}
      </h1>
      <div className="mb-6 flex justify-center relative items-center space-x-2 rounded-md pl-4 h-[48px] w-full bg-subprimary text-sm">
        <Icon icon={"lucide:search"} fontSize={18} className="text-textIcon" />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchInput}
          onChange={(e) => {
            const v = e.target.value;
            if (onSearchChange) handleSearchChange(v);
            else setSearchInput(v);
          }}
          className="outline-none flex-1 bg-inherit"
        />
      </div>
      <div className="bg-white space-y-7">
        {groupedData.length === 0 && (
          <EmptyState 
            title={t('common.noResults')}
            description={noDataMessage || t('processManagement.noDataFound')}
          />
        )}
        {groupedData.map((group: Group) => {
          return (
            <div
              key={group.groupId}
              className="px-4 py-3 rounded-lg border-l-[2.4px] border-darkBlue"
            >
              <h2 className="text-xl font-medium mb-3 text-black">
                {group.groupName}
              </h2>
              <div className="list-disc list-inside space-y-3">
                {(group.processes ?? []).map((process: Process) => {
                  const count = process.applicationCount ?? process.applicantProcesses?.length ?? 0;
                  return (
                    <div
                      key={process.processId}
                      className="text-black hover:text-[#001544] text-sm hover:underline space-y-3"
                    >
                      {count > 0 && (
                        <div className="flex space-x-2 items-center w-8/12">
                          <p className="bg-[#001A55] h-[24px] w-[24px] rounded-full flex justify-center items-center text-white text-[10px]">
                            {count}
                          </p>
                          <Link
                            to={`${baseRoute}/${process.processId}`}
                          >
                            {process.name}
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {meta && onPageChange && meta.totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center gap-2">
          <button
            onClick={() => onPageChange(meta.page - 1)}
            disabled={meta.page <= 1}
            className="px-4 py-2 rounded-md bg-subprimary disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {t('common.previous')}
          </button>
          <span className="text-sm text-gray-600">
            {meta.page} / {meta.totalPages}
          </span>
          <button
            onClick={() => onPageChange(meta.page + 1)}
            disabled={meta.page >= meta.totalPages}
            className="px-4 py-2 rounded-md bg-subprimary disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </main>
  );
};

export default ApplicationsList;
